<template>
  <div
    class="pusher-container"
    :class="[[PusherTabs.Info, PusherTabs.SelectToken].includes(activeTab) ? 'infoStepActive' : null]"
  >
    <div
      v-if="isGameStarted"
      class="skip-animation-area"
      :class="{ 'skip-animation-area-mobile': $device.isMobile }"
      @click="handleSkipAnimation"
    />
    <TavernHeaderLogo v-if="showTavernHeader" :checked-game="TavernGames.pusher" />
    <div
      v-if="!isRewind"
      class="wrapperContainer pusher"
      :class="{
        wrapperContainerAnimated: activeTab === PusherTabs.PlaceBet,
        winners: activeTab === PusherTabs.Winners,
        'winners-mobile': activeTab === PusherTabs.Winners && $device.isMobile
      }"
    >
      <component
        :is="activeComponent"
        v-model:token="token"
        v-model:activeTab="activeTab"
        :is-loading-token-config="isLoadingTokenConfig"
        :is-loading-pusher-stats="isLoadingPusherStats"
        @click-bet-selection="handleClickBetSelection"
        @get-out="handleGetOut"
      />
    </div>

    <TavernPusherSteps
      :show-login-required="!!address"
      :active="activeTab"
      :set-active-callback="setActiveTabClickHandler"
    />

    <el-dialog
      v-model="showModalResult"
      class="winLoseModal"
      :class="{
        'ru-locale': $i18n.locale === 'ru',
        winLoseModalMobile: $device.isMobile
      }"
      :fullscreen="$device.isMobile"
      :show-close="false"
      :append-to-body="true"
      :center="true"
      :modal="true"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <template #header>
        <img class="close" src="/img/icons/bone_close.svg" alt="close" @click="handleGetOutClick" />
      </template>
      <TavernPusherWinLose :token-address="tokenAddress" @go-to-stats="handleStatsClick" @get-out="handleGetOutClick" />
    </el-dialog>

    <el-dialog
      v-model="showModalBetSelection"
      class="pusherPlaceBetModal"
      :class="{
        'ru-locale': $i18n.locale === 'ru',
        pusherPlaceBetModalAbsolute: !$device.isMobile,
        pusherPlaceBetModalMobile: $device.isMobile
      }"
      :center="$device.isMobile"
      :append-to-body="$device.isMobile"
      :style="!$device.isMobile ? { height: '480px' } : {}"
      :show-close="false"
      :modal="false"
      destroy-on-close
    >
      <template #header>
        <img class="close" src="/img/icons/bone_close.svg" alt="close" @click="handleCloseBetSelection" />
      </template>
      <TavernPusherBetSelection :token="token" />
    </el-dialog>

    <el-dialog v-model="showModalStats" class="pusherPlaceBetModal">
      <TavernPusherStats />
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { apiUrls, Tokens } from '~/utils/constants';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import {
  PusherAnimations,
  type PusherConfigResponse,
  type PusherGameResultResponse,
  PusherGameStatus,
  type PusherStatsResponse,
  PusherTabs,
  type PusherUsersStatsItem,
  type PusherUsersStatsResponse
} from '~/types/Pusher';
import BigNumber from 'bignumber.js';
import { parseUnits } from 'ethers';
import type { IFlipCoinTokenConfig } from '~/types/apiService';
import { TavernGames } from '~/types/tavern';
import {
  ANIMATION_VIDEO_DURATION,
  BET_COUNT_MIN,
  COUNT_CARDS_IN_ROW,
  COUNT_CARDS_IN_ROW_MOBILE,
  GAME_RESULT_TIMEOUT,
  LIMIT
} from '~/utils/constants/pusher';

const { availableTokens } = useTokensReader();
const { isGameStarted, play, getLastAddedSPTimeAndMaxSP } = await usePusher();
const { apiUrl, blockchain } = useEnvs();
const { address } = useWeb3ModalAccount();
const { login } = useAuth();
const { isMobile } = useDevice();
const { t } = useI18n();

const delayNextAnimationInjected = inject<(ms: number) => Promise<void>>('delayNextAnimation');

const TavernPusherStats = resolveComponent('TavernPusherStats');
const TavernPusherTokensList = resolveComponent('TavernPusherTokensList');
const TavernPusherInfoModal = resolveComponent('TavernPusherInfoModal');
const TavernPusherPlaceBet = resolveComponent('TavernPusherPlaceBet');
const TavernPusherWinners = resolveComponent('TavernPusherWinners');

const emit = defineEmits([
  'start-new-animation',
  'delay-next-animation',
  'close-game',
  'set-background',
  'show-overlay'
]);

let resolvePromise: ((value: PusherGameResultResponse) => void) | null = null;
let timeoutId: ReturnType<typeof setTimeout> | null = null;

const animatedTabs = [PusherTabs.PlaceBet, PusherTabs.Pusher, PusherTabs.BetSelection, PusherTabs.Result];

const isRewind = ref(false);
const activeTab = ref(PusherTabs.SelectToken);
const betCount = ref(BET_COUNT_MIN);
const showModalStats = ref(false);
const showModalBetSelection = ref(false);
const showModalResult = ref(false);
const gameStatus = ref<PusherGameStatus>(PusherGameStatus.Idle);
const token = ref(Tokens.pearl);
const showOwn = ref(false);
const chunkedItemsPrev = ref<PusherUsersStatsItem[]>([]);
const offset = ref(0);
const page = ref(1);
const transactionHashRef = ref<string>();
const isButtonClicked = ref(false);
const lastSPInCurrentPeriod = ref<number[]>([]);
const skipAnimationResolve = ref<(() => void) | null>(null);
const isAnimationSkipped = ref(false);

const transactionHash = computed(() => transactionHashRef.value);
const showTavernHeader = computed(() => !(isMobile && animatedTabs.includes(activeTab.value)));

const isNotEnoughTokens = asyncComputed(async () => {
  if (!address.value) {
    return false;
  }

  const availableTokenAmount = await availableTokens(
    address.value,
    blockchain.contracts[token.value].addresses.contract
  );

  return BigNumber(availableTokenAmount).isZero() || Number(availableTokenAmount) < Number(betSize.value);
});

const { data: gameResult, refresh } = await useFetch<PusherGameResultResponse>(apiUrls.pusher.result, {
  baseURL: apiUrl,
  query: {
    transactionHash: transactionHash
  },
  watch: [transactionHash],
  key: 'pusherGameResult',
  immediate: false
});

const { pause, resume } = useIntervalFn(async () => {
  if (gameStatus.value === PusherGameStatus.Idle) {
    pause();
    return;
  }

  await refresh();

  if (gameResult.value && resolvePromise && !isLoadingTokenConfig.value) {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    pause();
    resolvePromise(gameResult.value);
  }
}, 2000);

const waitForGameResult = (): Promise<PusherGameResultResponse> =>
  new Promise<PusherGameResultResponse>((resolve, reject) => {
    resolvePromise = resolve;

    timeoutId = setTimeout(() => {
      pause();
      reject(new Error(t('errorHandleTransaction')));
    }, GAME_RESULT_TIMEOUT);

    resume();
  });

const load = useDebounceFn(() => {
  if (usersStatsData.value?.totalPages && usersStatsData.value.totalPages === page.value) {
    return;
  }

  offset.value += LIMIT;
  page.value += 1;
});

const betSize = computed(() => {
  if (!data.value?.betSize || !betCount.value) {
    return 0;
  }

  return betCount.value * data.value?.betSize;
});

const ticketPrice = computed(() => data.value?.betSize);

const tokenAddress = computed(() => blockchain.contracts[token.value].addresses.contract);

const query = computed(() => {
  const defaultQuery = {
    onlyWinners: true,
    limit: LIMIT,
    offset: offset.value
  };

  if (!showOwn.value) {
    return defaultQuery;
  }

  return {
    ...defaultQuery,
    userAddress: address.value
  };
});

// Fallback data for pusher stats
const fallbackPusherStats: PusherStatsResponse = {
  playersCount: 0,
  distributedPricePool: 0,
  prizePool: 0,
  placedBets: 0
};

const { status: pusherStatsStatus } = useFetch<PusherStatsResponse>(apiUrls.pusher.stats, {
  baseURL: apiUrl,
  watch: [address, gameStatus],
  query: {
    tokenAddress
  },
  key: 'pusherStats',
  immediate: true,
  default: () => fallbackPusherStats
});

// Fallback data for when the request fails
const fallbackPusherConfig: PusherConfigResponse = {
  prizePool: 0,
  betLimitResetTimestamp: 0,
  betSize: 0,
  winChance: 0,
  remainingBetLimit: 0,
  seasonPointsMultiplier: 0
};

const { data, status } = useFetch<PusherConfigResponse>(apiUrls.pusher.config, {
  baseURL: apiUrl,
  watch: [address, gameStatus, token],
  query: {
    userAddress: address,
    tokenAddress
  },
  key: 'pusherConfig',
  default: () => fallbackPusherConfig
});

// Fallback data for users stats
const fallbackUsersStats: PusherUsersStatsResponse = {
  total: 0,
  count: 0,
  pageNumber: 1,
  totalPages: 1,
  items: []
};

const { data: usersStatsData, status: usersStatsStatus } = useFetch<PusherUsersStatsResponse>(
  apiUrls.pusher.usersStats,
  {
    baseURL: apiUrl,
    watch: [address, showOwn, gameStatus],
    query,
    key: 'pusherUsersStats',
    default: () => fallbackUsersStats
  }
);

const isLoadingTokenConfig = computed(() => status.value === 'pending');
const isLoadingPusherStats = computed(() => pusherStatsStatus.value === 'pending');
const { data: tokensList } = useNuxtData<IFlipCoinTokenConfig[]>('tokensList');

const exchangeToken = computed(() => tokensList.value?.find((token) => token.tokenAddress === tokenAddress.value)!);

watch(
  [gameStatus, tokenAddress, address],
  async () => {
    lastSPInCurrentPeriod.value = (await getLastAddedSPTimeAndMaxSP(tokenAddress.value)).map((v) =>
      parseInt(v.toString())
    );
  },
  {
    immediate: true
  }
);

const pointsForBet = computed(() => {
  const totalAmountOfBetsInCurrentPeriod = BigNumber(parseUnits(betSize.value.toString() || '0', 'ether').toString())
    .plus(lastSPInCurrentPeriod.value[0])
    .toNumber();

  return getSPAmount(
    totalAmountOfBetsInCurrentPeriod,
    exchangeToken.value.seasonPointsMultiplier,
    lastSPInCurrentPeriod.value[2]
  );
});

const maxBets = asyncComputed(async () => {
  if (!address.value || !data.value?.betSize) {
    return 0;
  }

  const availableTokenAmount = await availableTokens(
    address.value,
    blockchain.contracts[token.value].addresses.contract
  );

  return Math.min(data.value.remainingBetLimit, Math.floor(Number(availableTokenAmount) / data.value.betSize));
});

const activeComponent = computed(() => {
  switch (activeTab.value) {
    case PusherTabs.Stats:
      return TavernPusherStats;
    case PusherTabs.SelectToken:
      return TavernPusherTokensList;

    case PusherTabs.PlaceBet:
    case PusherTabs.BetSelection:
    case PusherTabs.Result:
      return TavernPusherPlaceBet;
    case PusherTabs.Winners:
      return TavernPusherWinners;
    case PusherTabs.Info:
      return TavernPusherInfoModal;
    default:
      return TavernPusherPlaceBet;
  }
});

watch(maxBets, (value) => {
  // Reset input value in PlaceBetSelection when switching accounts
  betCount.value = value === 0 ? 0 : BET_COUNT_MIN;
});

watch(
  activeTab,
  async (value) => {
    switch (value) {
      case PusherTabs.Info:
      case PusherTabs.Stats:
      case PusherTabs.Winners:
      case PusherTabs.SelectToken:
        emit('set-background');
        break;
      case PusherTabs.PlaceBet:
        await nextTick();
        emit('start-new-animation', PusherAnimations.Idle, { duration: 0, loop: true, alignCenter: isMobile });
        break;
    }
  },
  {
    immediate: true
  }
);

watch([showOwn, address], () => {
  betCount.value = BET_COUNT_MIN;
  chunkedItemsPrev.value = [];
  offset.value = 0;
  page.value = 1;
});

const handleSkipAnimation = async () => {
  if (!skipAnimationResolve.value) {
    return;
  }

  isAnimationSkipped.value = true;
  skipAnimationResolve.value();
  skipAnimationResolve.value = null;

  resolvePromise = null;
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }

  isGameStarted.value = false;
  isButtonClicked.value = false;
  gameStatus.value = PusherGameStatus.Finished;

  resume();

  await nextTick();
  setActiveTab(PusherTabs.SelectToken);
  emit('start-new-animation', PusherAnimations.Idle, { duration: 0, loop: true, alignCenter: isMobile });
};

const startGame = async () => {
  try {
    isButtonClicked.value = true;
    gameStatus.value = PusherGameStatus.Launched;
    isAnimationSkipped.value = false;

    const txHash = await play(betCount.value, tokenAddress.value);

    if (!isGameStarted.value || !delayNextAnimationInjected || !txHash) {
      return;
    }

    transactionHashRef.value = txHash;

    await nextTick();
    await refresh();

    showModalBetSelection.value = false;

    setActiveTab(PusherTabs.Pusher);

    let resolveSkipAnimation: () => void;
    const skipAnimationPromise = new Promise<void>((resolve) => {
      resolveSkipAnimation = resolve;
    });

    skipAnimationResolve.value = resolveSkipAnimation!;

    emit('start-new-animation', PusherAnimations.BeforeResult, { duration: 0, alignCenter: isMobile });

    await Promise.race([delayNextAnimationInjected(ANIMATION_VIDEO_DURATION), skipAnimationPromise]);

    if (isAnimationSkipped.value) {
      return;
    }

    emit('start-new-animation', PusherAnimations.Await, { duration: 0, alignCenter: isMobile, loop: true });

    const raceResult = await Promise.race([waitForGameResult(), skipAnimationPromise]);

    if (isAnimationSkipped.value) {
      return;
    }

    let result: PusherGameResultResponse | undefined;

    if (raceResult && 'isWinner' in raceResult) {
      result = raceResult;
    } else {
      result = await waitForGameResult();
    }

    if (!result || isAnimationSkipped.value) {
      return;
    }

    if (result?.isWinner) {
      emit('start-new-animation', PusherAnimations.Win, { duration: 0.25, alignCenter: isMobile });
    } else {
      emit('start-new-animation', PusherAnimations.Lose, { duration: 0.25, alignCenter: isMobile });
    }

    await Promise.race([delayNextAnimationInjected(2000), skipAnimationPromise]);

    if (isAnimationSkipped.value) {
      return;
    }

    gameStatus.value = PusherGameStatus.Finished;
    showModalResult.value = true;
    setActiveTab(PusherTabs.Result);

    await Promise.race([delayNextAnimationInjected(2000), skipAnimationPromise]);

    if (isAnimationSkipped.value) {
      return;
    }

    if (!animatedTabs.includes(activeTab.value)) {
      return;
    }

    setActiveTab(PusherTabs.PlaceBet);
    emit('start-new-animation', PusherAnimations.Idle, { duration: 0, loop: true, alignCenter: isMobile });
  } catch (error: unknown) {
    console.log(error);
  } finally {
    isGameStarted.value = false;
    isButtonClicked.value = false;
    skipAnimationResolve.value = null;
    isAnimationSkipped.value = false;
  }
};

const handleClickMin = () => {
  betCount.value = BET_COUNT_MIN;
};

const handleClickMax = () => {
  if (!data.value?.remainingBetLimit) {
    return;
  }

  betCount.value = data.value.remainingBetLimit;
};

const handleClickBetSelection = async () => {
  if (!address.value) {
    await login();
    return;
  }

  if (gameStatus.value === PusherGameStatus.Launched) {
    return;
  }

  showModalBetSelection.value = true;
  setActiveTab(PusherTabs.BetSelection);
};

const handleGetOut = () => {
  setActiveTab(PusherTabs.PlaceBet);
};

const handleCloseBetSelection = () => {
  showModalBetSelection.value = false;
  betCount.value = BET_COUNT_MIN;
  setActiveTab(PusherTabs.PlaceBet);
};

const handleStatsClick = () => {
  showModalResult.value = false;
  setActiveTab(PusherTabs.Stats);
};

const handleGetOutClick = () => {
  showModalResult.value = false;
  setActiveTab(PusherTabs.PlaceBet);
};

const setActiveTab = (value: number) => {
  activeTab.value = value;
};

const setActiveTabClickHandler = (step: number) => {
  if (gameStatus.value === PusherGameStatus.Launched) {
    return;
  }

  if ([PusherTabs.BetSelection, PusherTabs.Pusher, PusherTabs.Result].includes(step)) {
    return;
  }

  if (step === PusherTabs.SelectGame) {
    emit('close-game');
    return;
  }

  if (showModalBetSelection.value) {
    showModalBetSelection.value = false;
  }

  activeTab.value = step;
};

function chunkArray(array: PusherUsersStatsItem[], size: number) {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

const chunkedItems = computed(() => {
  if (!usersStatsData.value || !usersStatsData.value?.items) {
    return [];
  }

  return chunkArray(chunkedItemsPrev.value, isMobile ? COUNT_CARDS_IN_ROW_MOBILE : COUNT_CARDS_IN_ROW);
});

watch(
  usersStatsData,
  (newValue) => {
    if (!newValue?.items) {
      return;
    }

    if (activeTab.value !== PusherTabs.Winners) {
      chunkedItemsPrev.value = [];
    }

    if (chunkedItemsPrev.value) {
      chunkedItemsPrev.value = [...chunkedItemsPrev.value, ...newValue.items];
      return chunkArray(chunkedItemsPrev.value, isMobile ? COUNT_CARDS_IN_ROW_MOBILE : COUNT_CARDS_IN_ROW);
    }

    chunkedItemsPrev.value = newValue.items;
  },
  {
    immediate: true
  }
);

provide('pusherGameStatus', gameStatus);
provide('pusherBetCount', betCount);
provide('pusherBetSize', betSize);
provide('pusherTicketPrice', ticketPrice);
provide('pusherMaxBets', maxBets);
provide('showModalBetSelection', showModalBetSelection);
provide('pusherStartGame', startGame);
provide('pusherClickMax', handleClickMax);
provide('pusherClickMin', handleClickMin);
provide('pusherUserStatsOffset', offset);
provide('pusherUserStatsLoad', load);
provide('pusherUserStatsShowOwn', showOwn);
provide('pusherUserStatsData', chunkedItems);
provide('pusherUsersStatsStatus', usersStatsStatus);
provide('pusherPointsForBet', pointsForBet);
provide('pusherIsButtonClicked', isButtonClicked);
provide('pusherIsNotEnoughTokens', isNotEnoughTokens);
</script>

<style lang="scss" scoped>
.pusher-container {
  position: relative;

  .logoContainer {
    top: -90px;
  }

  .skip-animation-area {
    position: absolute;
    width: 50%;
    height: 100%;
    top: -20px;
    right: 50px;
    cursor: pointer;
  }

  .skip-animation-area-mobile {
    width: 100%;
    top: 55px;
    right: 0;
    height: calc(100dvh - 230px);
  }
}

.infoStepActive {
  background-color: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(8px);
}

.wrapperContainer {
  height: 100%;
  padding-left: 0;
  padding-right: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  :deep(.dropdown) {
    background: rgba(50, 31, 25, 1);
    width: max-content;
    min-width: 200px;
    border-color: rgba(72, 59, 32, 1);
    border-radius: 10px;
    font-size: 16px;
    font-weight: 500;
    font-family: Eczar, sans-serif;
    z-index: 9999;

    @media screen and (max-width: 479px) {
      position: relative;
      width: 100%;
      max-width: 50%;
      top: 0;
      left: 0;
    }

    &.expanded {
      border-radius: 10px 10px 0 0;
    }

    .menuList {
      border: 2px solid rgba(72, 59, 32, 1);
      top: 28px;
    }

    .checkedItem {
      padding: 0 16px;
      height: 30px;
      color: #fff;
      img {
        width: 16px;
      }
    }

    .menuListItem {
      padding: 0 16px;
      height: 30px;
      background: rgba(50, 31, 25, 1);
      color: #a49686;

      &:not(:last-child) {
        border-bottom-width: 0;
      }
    }
  }
}

.wrapperContainerAnimated {
  background: none;
  backdrop-filter: none;
}

.winners {
  padding-left: 30px;
  padding-right: 30px;
  padding-bottom: 0;
  height: 388px;
}

.winners-mobile {
  padding-left: 14px;
  padding-right: 14px;
  padding-bottom: 0;
  height: calc(100dvh - 240px);
}

.title {
  margin: 0 auto 15px;
  font-size: 30px;
  font-weight: 700;
  color: #fff;
  font-family: Grenze, sans-serif;
  text-align: center;
}

.totalSPInfo {
  padding: 3px 9px;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  border: 1px solid #201a0e;
  border-radius: 12px;
  background-color: #07000080;
  gap: 10px;
  font-family: Eczar, sans-serif;
  font-size: 16px;
  color: #a2a2a2;
  font-weight: 500;

  @media screen and (max-width: 479px) {
    left: 5px;
    &.right-mobile {
      left: unset;
      right: 5px;
    }
  }

  .amount {
    color: #fff;
    white-space: nowrap;
  }

  img {
    width: 26px;
  }

  & > p {
    display: flex;
    align-items: center;
    gap: 5px;
  }
}

.placeBetBtn {
  @media screen and (max-width: 479px) {
    margin-bottom: 14px;
  }
}

.close {
  width: 20px;
  height: 20px;
  position: absolute;
  right: 25px;
  top: 25px;
  cursor: pointer;
}
</style>

<style lang="scss">
.wrapperContainer.pusher {
  .gradient-button {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .whiteMainTitle {
    font-family: Grenze;
    font-weight: 700;
    font-size: 30px;
    line-height: 1;
    color: var(--main-text-color);
    text-align: center;
  }

  .whiteTitle {
    font-family: Eczar;
    font-weight: 600;
    font-size: 20px;
    line-height: 1.2;
    color: var(--main-text-color);
  }

  .whiteTitleMedium {
    font-family: Eczar;
    font-weight: 500;
    font-size: 20px;
    line-height: 1.2;
    color: var(--main-text-color);
  }

  .whiteTitleBold {
    font-family: Eczar;
    font-weight: 700;
    font-size: 20px;
    line-height: 1;
    color: var(--main-text-color);
  }

  .greyTitle {
    font-family: Eczar;
    font-weight: 500;
    font-size: 20px;
    line-height: 1.2;
    color: var(--dark-6);
  }

  .orangeTitle {
    font-family: Eczar;
    font-weight: 600;
    font-size: 20px;
    line-height: 1;
    color: var(--orange);
  }

  .orangeTitleBold {
    font-family: Eczar;
    font-weight: 700;
    font-size: 20px;
    line-height: 1;
    color: var(--orange);
  }

  .redTitleLarge {
    font-family: Eczar;
    font-weight: 600;
    font-size: 32px;
    line-height: 1;
    color: red;
  }

  .orangeTitleLarge {
    font-family: Eczar;
    font-weight: 600;
    font-size: 32px;
    line-height: 1;
    color: var(--orange);
  }

  .greyTitleBold {
    font-family: Eczar;
    font-weight: 600;
    font-size: 20px;
    line-height: 1.2;
    color: rgba(162, 162, 162, 1);
  }

  .img-sm {
    height: 30px;
    width: 30px;
    max-width: 30px;
    max-height: 30px;
  }

  .img-md {
    height: 32px;
    width: 32px;
    max-width: 32px;
    max-height: 32px;
  }

  .img-lg {
    height: 40px;
    width: 40px;
    max-width: 40px;
    max-height: 40px;
  }

  .img-xl {
    height: 50px;
    width: 50px;
    max-width: 50px;
    max-height: 50px;
  }

  .pusherPlaceBet {
    cursor: default;

    .gradient-button {
      max-width: unset;
      min-width: 266px;
    }
  }
}

.el-dialog.pusherPlaceBetModal {
  .el-dialog__body {
    margin: 0;
  }

  width: 461px;
  height: 454px;
  border-radius: 12px;
  background: linear-gradient(to bottom, #4e3127, #000000);
  padding: 10px 21px 14px 21px;
  z-index: 9999;
  position: absolute;
  left: 25%;
  border: 1px solid #483b20;
}

.el-dialog.pusherPlaceBetModal.pusherPlaceBetModalAbsolute {
  width: 390px;
  top: 50%;
  transform: translateY(-50%);
  position: absolute;
  left: 36px;
  margin: 0;
  background: linear-gradient(to bottom, rgba(67, 42, 34, 0.25), rgba(1, 0, 0, 0.25));
  backdrop-filter: blur(4px);
  border: none;

  .el-dialog__body {
    padding-top: 32px;
  }
}

.el-dialog.pusherPlaceBetModalMobile {
  width: 100%;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  margin-top: 0px;
  height: 520px;
}

.el-dialog.winLoseModal {
  width: 435px;
  height: 500px;
  min-height: 500px;
  border-radius: 12px;
  padding-top: 7px;
  border: 1px solid #483b20;
  margin: 0;
  overflow: hidden;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.el-dialog.winLoseModalMobile {
  overflow: hidden;
  min-height: unset;
  width: 100%;
  min-width: unset;
  height: 100dvh;
}

.pusher-container {
  .logoContainer img {
    height: 35px;
    width: 35px;
    bottom: -50%;
  }
}
</style>
